import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/lib/utils';
import { EmptyState } from './empty-state';
import { Skeleton } from '../feedback/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';

export type ResponsiveTableColumnSize =
  | 'flex'
  | 'identity'
  | 'medium'
  | 'wide'
  | 'date'
  | 'status'
  | 'compact'
  | 'micro'
  | 'tiny'
  | 'actionCompact'
  | 'action'
  | 'actionWide';
export type ResponsiveTableColumnLayout = 'auto' | 'stable';

export type ResponsiveTableColumn<T> = {
  key: string;
  header: React.ReactNode;
  /** Optional mobile control for a column-level action such as sorting. */
  mobileControl?: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  size?: ResponsiveTableColumnSize;
  className?: string;
  headerClassName?: string;
};

export type ResponsiveTableRowGroup = {
  key: string;
  label: React.ReactNode;
};

export type ResponsiveTableEmptyState = {
  description?: React.ReactNode;
  title: React.ReactNode;
};

/** Optional, consumer-owned row navigation. */
export type ResponsiveTableRowNavigation = {
  ariaLabel: string;
  onNavigate: () => void;
};

type ResponsiveTableBreakpoint = 'sm' | 'md' | 'lg' | 'xl';

interface ResponsiveTableProps<T> {
  rows: T[];
  columns: readonly ResponsiveTableColumn<T>[];
  renderMobileCard: (row: T) => React.ReactNode;
  getRowKey?: (row: T, index: number) => string;
  getRowGroup?: (row: T, index: number) => ResponsiveTableRowGroup | null;
  getRowNavigation?: (row: T, index: number) => ResponsiveTableRowNavigation | null;
  caption?: React.ReactNode;
  /** Optional desktop table footer (compose TableFooter/rows/cells). Mobile summary is caller-owned. */
  footer?: React.ReactNode;
  tableClassName?: string;
  tableHeaderClassName?: string;
  mobileClassName?: string;
  /** Accessible label for the optional mobile column controls. */
  mobileControlsLabel?: string;
  breakpointClassName?: ResponsiveTableBreakpoint;
  /** Opt in to actual container/table fit; cards are the safe unmeasured fallback. */
  responsiveMode?: 'breakpoint' | 'container';
  isLoading?: boolean;
  loadingLabel?: string;
  loadingRowCount?: number;
  /**
   * Scope for transient measured loading geometry. A caller must change this
   * whenever rows from another access scope or view could be rendered.
   */
  loadingGeometryKey?: string;
  /** Opt-in desktop spacing; mobile cards and footer composition remain caller-owned. */
  density?: 'default' | 'compact';
  columnLayout?: ResponsiveTableColumnLayout;
  ariaLabel?: string;
  /**
   * Shared empty treatment shown instead of an empty table or mobile-card shell.
   * Set false only when zero rows represent a non-empty range state (for example,
   * a stale pagination cursor) rather than a collection result.
   */
  emptyState?: ResponsiveTableEmptyState | false;
}

const stableColumnSizeClasses: Record<ResponsiveTableColumnSize, { column: string; cell: string }> =
  {
    flex: {
      column: 'w-auto',
      cell: 'min-w-0 overflow-hidden',
    },
    identity: {
      column: 'w-40',
      cell: 'w-40 max-w-40 overflow-hidden',
    },
    medium: {
      column: 'w-56',
      cell: 'w-56 max-w-56 overflow-hidden',
    },
    wide: {
      column: 'w-72',
      cell: 'w-72 max-w-72 overflow-hidden',
    },
    date: {
      column: 'w-44',
      cell: 'w-44 max-w-44 overflow-hidden',
    },
    status: {
      column: 'w-40',
      cell: 'w-40 max-w-40 overflow-hidden',
    },
    compact: {
      column: 'w-32',
      cell: 'w-32 max-w-32 overflow-hidden',
    },
    micro: {
      column: 'w-24',
      cell: 'w-24 max-w-24 overflow-hidden',
    },
    tiny: {
      column: 'w-20',
      cell: 'w-20 max-w-20 overflow-hidden',
    },
    actionCompact: {
      column: 'w-20',
      cell: 'w-20 max-w-20 overflow-hidden',
    },
    action: {
      column: 'w-28',
      cell: 'w-28 max-w-28 overflow-hidden',
    },
    actionWide: {
      column: 'w-40',
      cell: 'w-40 max-w-40 overflow-hidden',
    },
  };

/**
 * ResponsiveTable
 *
 * Renders a table on desktop and cards on mobile using shared UI primitives.
 */
export function ResponsiveTable<T>({
  rows,
  columns,
  renderMobileCard,
  getRowKey,
  getRowGroup,
  getRowNavigation,
  caption,
  footer,
  tableClassName,
  tableHeaderClassName,
  mobileClassName,
  mobileControlsLabel = 'Table controls',
  breakpointClassName = 'md',
  responsiveMode = 'breakpoint',
  isLoading = false,
  loadingLabel = 'Loading',
  loadingRowCount = 5,
  loadingGeometryKey,
  density = 'default',
  columnLayout = 'auto',
  ariaLabel,
  emptyState,
}: ResponsiveTableProps<T>) {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const [tableFits, setTableFits] = React.useState(false);
  const loadedTableWidth = React.useRef<{
    key: string | undefined;
    density: 'default' | 'compact';
    width: number;
  } | null>(null);
  const useContainerFit = responsiveMode === 'container';
  // Keep the same intrinsically sized table measurable while cards are shown.
  // Its layout never depends on tableFits, avoiding a table/card feedback loop.
  React.useLayoutEffect(() => {
    if (!useContainerFit || typeof ResizeObserver === 'undefined') return;
    const container = containerRef.current;
    const table = tableRef.current;
    if (!container || !table) return;
    const measure = () => {
      const available = container.getBoundingClientRect().width;
      let required = table.getBoundingClientRect().width;
      const loaded = loadedTableWidth.current;
      if (
        isLoading &&
        loaded !== null &&
        loaded.key === loadingGeometryKey &&
        loaded.density === density
      ) {
        required = Math.max(required, loaded.width);
      } else if (!isLoading && required > 0) {
        loadedTableWidth.current = { key: loadingGeometryKey, density, width: required };
      }
      // A full-width table and its flex/grid container can differ by subpixel rounding.
      setTableFits(available > 0 && required > 0 && required <= available + 0.5);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(table);
    return () => {
      observer.disconnect();
    };
  }, [useContainerFit, columns, rows, isLoading, loadingGeometryKey, density]);
  const tableRowElements = React.useRef(new Map<number, HTMLTableRowElement>());
  const mobileCardElements = React.useRef(new Map<number, HTMLDivElement>());
  const measuredGeometry = React.useRef<
    | {
        cardHeights: readonly number[];
        key: string;
        tableRowHeights: readonly number[];
      }
    | undefined
  >(undefined);
  const [viewportWidth, setViewportWidth] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => {
      window.removeEventListener('resize', updateViewportWidth);
    };
  }, []);

  const geometryKey = `${loadingGeometryKey ?? 'default'}:${density}:${String(viewportWidth ?? 'unmeasured')}`;
  const compactDensity = density === 'compact';
  const densityCellClasses = compactDensity ? 'px-3 py-3' : undefined;
  const captureLoadedGeometry = React.useCallback(() => {
    const tableRowHeights = rows.map((_, index) => {
      const row = tableRowElements.current.get(index);
      if (row === undefined) return 0;
      return row.getBoundingClientRect().height;
    });
    const cardHeights = rows.map(
      (_, index) => mobileCardElements.current.get(index)?.getBoundingClientRect().height ?? 0
    );
    if (tableRowHeights.some((height) => height > 0) || cardHeights.some((height) => height > 0)) {
      measuredGeometry.current = { cardHeights, key: geometryKey, tableRowHeights };
    }
  }, [geometryKey, rows]);
  const resolvedTableClassName = cn(
    tableClassName ?? 'border border-border table-auto',
    columnLayout === 'stable' && 'table-fixed',
    useContainerFit && 'w-max min-w-full table-auto'
  );
  const resolvedTableHeaderClassName = tableHeaderClassName ?? 'bg-[hsl(var(--surface-subtle))]';
  const headerCellClasses = 'whitespace-normal break-normal hyphens-none';
  const textCellClasses = 'whitespace-normal break-words hyphens-auto';
  const tokenCellClasses = 'max-w-[250px] whitespace-normal break-words hyphens-auto';
  const mobileControls = columns.flatMap((column) =>
    column.mobileControl === undefined ? [] : [{ control: column.mobileControl, key: column.key }]
  );

  const breakpointClasses: Record<ResponsiveTableBreakpoint, { hidden: string; visible: string }> =
    {
      sm: { hidden: 'hidden sm:block', visible: 'sm:hidden' },
      md: { hidden: 'hidden md:block', visible: 'md:hidden' },
      lg: { hidden: 'hidden lg:block', visible: 'lg:hidden' },
      xl: { hidden: 'hidden xl:block', visible: 'xl:hidden' },
    };
  const { hidden: breakpointHidden, visible: breakpointVisible } =
    breakpointClasses[breakpointClassName];

  const isTokenColumnKey = (key: string): boolean =>
    /(^|_)(id|uuid|token|key|hash|secret)($|_)/i.test(key);

  const insertSoftBreaks = (value: string): string => {
    const withUnderscoreBreaks = value.replaceAll('_', '_\u200B');
    if (/\s/.test(withUnderscoreBreaks)) return withUnderscoreBreaks;
    if (withUnderscoreBreaks.length <= 16) return withUnderscoreBreaks;
    return withUnderscoreBreaks.replace(/(.{8})/g, '$1\u200B');
  };

  const renderCellContent = (value: React.ReactNode, isTokenColumn: boolean) => {
    if (typeof value === 'string' || typeof value === 'number') {
      const raw = String(value);
      const formatted = isTokenColumn ? insertSoftBreaks(raw) : raw;
      return <span>{formatted}</span>;
    }
    return value;
  };

  React.useLayoutEffect(() => {
    if (isLoading) return;
    captureLoadedGeometry();
  }, [captureLoadedGeometry, isLoading]);

  React.useEffect(() => {
    if (isLoading || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(captureLoadedGeometry);
    for (const row of tableRowElements.current.values()) observer.observe(row);
    for (const card of mobileCardElements.current.values()) observer.observe(card);
    return () => {
      observer.disconnect();
    };
  }, [captureLoadedGeometry, isLoading]);

  const loadingGeometry =
    isLoading && measuredGeometry.current?.key === geometryKey
      ? measuredGeometry.current
      : undefined;
  const resolvedLoadingRowCount =
    loadingGeometry?.tableRowHeights.length ||
    loadingGeometry?.cardHeights.length ||
    loadingRowCount;
  const loadingRows = Array.from({ length: resolvedLoadingRowCount }, (_, index) => index);

  const isInteractiveTarget = (target: EventTarget | null, container: HTMLElement): boolean => {
    if (!(target instanceof Element)) return false;
    const interactive = target.closest(
      'a, button, input, select, textarea, label, summary, [role="button"], [role="checkbox"], [role="link"], [role="menuitem"], [contenteditable="true"]'
    );
    return interactive !== null && interactive !== container;
  };

  const hasActiveTextSelection = (): boolean => {
    const selection = window.getSelection();
    return selection !== null && !selection.isCollapsed && selection.toString().trim().length > 0;
  };

  const navigateFromPointer = (
    event: React.MouseEvent<HTMLElement>,
    navigation: ResponsiveTableRowNavigation
  ) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      !event.currentTarget.contains(event.target as Node) ||
      isInteractiveTarget(event.target, event.currentTarget) ||
      hasActiveTextSelection()
    ) {
      return;
    }
    navigation.onNavigate();
  };

  const navigateFromKeyboard = (
    event: React.KeyboardEvent<HTMLElement>,
    navigation: ResponsiveTableRowNavigation
  ) => {
    if (
      (event.key !== 'Enter' && event.key !== ' ') ||
      event.defaultPrevented ||
      !event.currentTarget.contains(event.target as Node) ||
      isInteractiveTarget(event.target, event.currentTarget)
    ) {
      return;
    }
    event.preventDefault();
    navigation.onNavigate();
  };

  const resolvedEmptyState = emptyState ?? {
    title: t('common.table.emptyTitle', 'No records available.'),
    description: t(
      'common.table.emptyDescription',
      'Try changing your search or filters to find what you need.'
    ),
  };

  if (!isLoading && rows.length === 0 && resolvedEmptyState !== false) {
    return (
      <EmptyState title={resolvedEmptyState.title} description={resolvedEmptyState.description} />
    );
  }

  const content = (
    <>
      {isLoading ? (
        <span role="status" aria-live="polite" className="sr-only">
          {loadingLabel}
        </span>
      ) : null}
      <div
        className={cn(
          'relative space-y-3 min-w-0 break-words hyphens-auto',
          useContainerFit ? (tableFits ? 'hidden' : 'block') : breakpointVisible,
          mobileClassName
        )}
        aria-busy={isLoading}
        data-testid="responsive-table-cards"
      >
        {mobileControls.length > 0 ? (
          <div
            className="flex flex-wrap gap-3"
            role="group"
            aria-label={mobileControlsLabel}
            data-slot="responsive-table-mobile-controls"
          >
            {mobileControls.map(({ control, key }) => (
              <span key={key}>{control}</span>
            ))}
          </div>
        ) : null}
        {isLoading
          ? loadingRows.map((index) => (
              <div
                key={`loading-card-${String(index)}`}
                data-slot="responsive-table-loading-card"
                className="space-y-4 rounded-lg border border-border bg-card p-4"
                style={
                  loadingGeometry?.cardHeights[index]
                    ? { height: `${String(loadingGeometry.cardHeights[index])}px` }
                    : undefined
                }
                aria-hidden="true"
              >
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </div>
            ))
          : rows.map((row, index) => {
              const group = getRowGroup?.(row, index) ?? null;
              const previousGroup =
                index === 0 ? null : (getRowGroup?.(rows[index - 1], index - 1) ?? null);
              const showGroup = group !== null && group.key !== previousGroup?.key;
              const rowNavigation = getRowNavigation?.(row, index) ?? null;
              const mobileCardContent = renderMobileCard(row);
              const mobileRowRef = (element: HTMLDivElement | null) => {
                if (element) mobileCardElements.current.set(index, element);
                else mobileCardElements.current.delete(index);
              };
              return (
                <React.Fragment key={getRowKey?.(row, index) ?? String(index)}>
                  {showGroup ? (
                    <div
                      className="px-1 pt-2 text-sm font-semibold text-foreground"
                      data-slot="responsive-table-row-group"
                    >
                      {group.label}
                    </div>
                  ) : null}
                  {rowNavigation ? (
                    <div
                      ref={mobileRowRef}
                      className="min-w-0 cursor-pointer hyphens-auto break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-row-index={index}
                      data-slot="responsive-table-row-navigation"
                      role="link"
                      tabIndex={0}
                      aria-label={rowNavigation.ariaLabel}
                      onClick={(event) => {
                        navigateFromPointer(event, rowNavigation);
                      }}
                      onKeyDown={(event) => {
                        navigateFromKeyboard(event, rowNavigation);
                      }}
                    >
                      {mobileCardContent}
                    </div>
                  ) : (
                    <div
                      ref={mobileRowRef}
                      className="min-w-0 hyphens-auto break-words"
                      data-row-index={index}
                    >
                      {mobileCardContent}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
      </div>

      <div
        className={cn(
          'relative max-w-full overflow-x-auto',
          useContainerFit
            ? tableFits
              ? 'block'
              : 'invisible absolute inset-x-0 top-0 pointer-events-none'
            : breakpointHidden
        )}
        aria-hidden={useContainerFit && !tableFits ? true : undefined}
        inert={useContainerFit && !tableFits ? true : undefined}
        aria-busy={isLoading}
        data-testid="responsive-table-table"
      >
        <Table ref={tableRef} aria-label={ariaLabel} className={resolvedTableClassName}>
          {caption ? <TableCaption>{caption}</TableCaption> : null}
          {columnLayout === 'stable' ? (
            <colgroup data-slot="responsive-table-column-layout">
              {columns.map((column) => {
                const columnSize = column.size ?? 'flex';
                return (
                  <col
                    key={column.key}
                    className={stableColumnSizeClasses[columnSize].column}
                    data-column-key={column.key}
                    data-column-size={columnSize}
                  />
                );
              })}
            </colgroup>
          ) : null}
          <TableHeader className={resolvedTableHeaderClassName}>
            <TableRow>
              {columns.map((column) => {
                const isTokenColumn = isTokenColumnKey(column.key);
                const columnSize = column.size ?? 'flex';
                return (
                  <TableHead
                    key={column.key}
                    data-column-key={column.key}
                    data-column-size={columnLayout === 'stable' ? columnSize : undefined}
                    className={cn(
                      headerCellClasses,
                      compactDensity && 'px-3',
                      columnLayout === 'stable' && stableColumnSizeClasses[columnSize].cell,
                      column.headerClassName
                    )}
                  >
                    {renderCellContent(column.header, isTokenColumn)}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? loadingRows.map((rowIndex) => (
                  <TableRow
                    key={`loading-row-${String(rowIndex)}`}
                    data-slot="responsive-table-loading-row"
                    aria-hidden="true"
                    style={
                      loadingGeometry?.tableRowHeights[rowIndex]
                        ? { height: `${String(loadingGeometry.tableRowHeights[rowIndex])}px` }
                        : undefined
                    }
                  >
                    {columns.map((column, columnIndex) => {
                      const isTokenColumn = isTokenColumnKey(column.key);
                      const columnSize = column.size ?? 'flex';
                      const loadingRowHeight = loadingGeometry?.tableRowHeights[rowIndex];
                      return (
                        <TableCell
                          key={column.key}
                          data-column-key={column.key}
                          data-column-size={columnLayout === 'stable' ? columnSize : undefined}
                          style={
                            loadingRowHeight === undefined
                              ? undefined
                              : { height: '100%', padding: 0 }
                          }
                          className={cn(
                            isTokenColumn ? tokenCellClasses : textCellClasses,
                            densityCellClasses,
                            columnLayout === 'stable' && stableColumnSizeClasses[columnSize].cell,
                            loadingRowHeight !== undefined && 'p-0',
                            column.className
                          )}
                        >
                          <div
                            className={cn(
                              loadingRowHeight !== undefined && [
                                'flex h-full items-center',
                                compactDensity ? 'px-3' : 'px-4',
                              ],
                              loadingRowHeight === undefined && 'contents'
                            )}
                          >
                            <Skeleton
                              className={cn(
                                'h-5',
                                columnIndex === 0 ? 'w-3/4' : 'w-1/2',
                                columnSize === 'action' && 'w-8'
                              )}
                            />
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : rows.map((row, index) => {
                  const group = getRowGroup?.(row, index) ?? null;
                  const previousGroup =
                    index === 0 ? null : (getRowGroup?.(rows[index - 1], index - 1) ?? null);
                  const showGroup = group !== null && group.key !== previousGroup?.key;
                  const rowNavigation = getRowNavigation?.(row, index) ?? null;
                  return (
                    <React.Fragment key={getRowKey?.(row, index) ?? String(index)}>
                      {showGroup ? (
                        <TableRow data-slot="responsive-table-row-group">
                          <TableCell
                            className="bg-[hsl(var(--surface-subtle))] py-2 text-sm font-semibold"
                            colSpan={columns.length}
                          >
                            {group.label}
                          </TableCell>
                        </TableRow>
                      ) : null}
                      <TableRow
                        ref={(element) => {
                          if (element) tableRowElements.current.set(index, element);
                          else tableRowElements.current.delete(index);
                        }}
                        data-row-index={index}
                        className={cn(
                          rowNavigation &&
                            'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'
                        )}
                        data-slot={rowNavigation ? 'responsive-table-row-navigation' : undefined}
                        role={rowNavigation ? 'link' : undefined}
                        tabIndex={rowNavigation ? 0 : undefined}
                        aria-label={rowNavigation?.ariaLabel}
                        onClick={
                          rowNavigation
                            ? (event) => {
                                navigateFromPointer(event, rowNavigation);
                              }
                            : undefined
                        }
                        onKeyDown={
                          rowNavigation
                            ? (event) => {
                                navigateFromKeyboard(event, rowNavigation);
                              }
                            : undefined
                        }
                      >
                        {columns.map((column) => {
                          const isTokenColumn = isTokenColumnKey(column.key);
                          const columnSize = column.size ?? 'flex';
                          return (
                            <TableCell
                              key={column.key}
                              data-column-key={column.key}
                              data-column-size={columnLayout === 'stable' ? columnSize : undefined}
                              className={cn(
                                isTokenColumn ? tokenCellClasses : textCellClasses,
                                densityCellClasses,
                                columnLayout === 'stable' &&
                                  stableColumnSizeClasses[columnSize].cell,
                                column.className
                              )}
                            >
                              {renderCellContent(column.cell(row), isTokenColumn)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </React.Fragment>
                  );
                })}
          </TableBody>
          {!isLoading ? footer : null}
        </Table>
      </div>
    </>
  );
  return useContainerFit ? (
    <div ref={containerRef} className="relative min-w-0 w-full">
      {content}
    </div>
  ) : (
    content
  );
}
